import ContractlistState, {ArchivedState, ListContract} from "./ContractlistState";
import {AnyAction} from "redux";
import {ServerHolder} from "./components/contractlist2";
import {ThunkDispatch} from "redux-thunk";
import {IHttpResponse} from "angular";
import {Contract, ContractType} from "../../../integration/representation/contract";
import {Fylke} from "../../../integration/nvdbapi/nvdbapi";
import {getContractsCheckedForChanges, stateContracts} from "./selectors";

export enum ContractlistActionType {
    FETCHING_CONTRACTS,
    SET_CONTRACTS,
    SET_PAGE,
    SET_SORT_FIELD,
    SET_SORT_ASCENDING,
    SET_SEARCH_STRING,
    SET_FILTER_BY_TYPE,
    SET_FILTER_BY_CURRENT_USER,
    SET_CURRENT_USER,
    SET_ARCHIVED_FILTER,
    SET_CONTRACTS_WITH_CHANGE_INDICATOR,
    ADD_CONTRACTS_WITH_CHANGE_INDICATOR,
    SET_TOTAL_NUMBER_OF_CONTRACTS_WITH_FILTER,
}

export type ContractListThunk = (dispatch: ThunkDispatch<ContractlistState, ServerHolder, AnyAction>, getState: () => ContractlistState, serverHolder: ServerHolder) => void


const showErrorMessage = (message: string): ContractListThunk => (dispatch, getState, serverHolder) => {
    serverHolder.server.messages.danger(message);
    serverHolder.scope.$digest();
};

export type InitContractList = () => ContractListThunk;
export const initContractList: InitContractList = () => (dispatch, getState, serverHolder) => {
    const {page, showArchivedContracts, typeFilter, searchString, showOtherPeoplesContracts, sortByField, isAscending} = getState()
    serverHolder.security.ensureCurrentUser()
        .then(user => {
            dispatch({
                type: ContractlistActionType.SET_CURRENT_USER,
                currentUser: user
            });
            dispatch(fetchContracts(page, showArchivedContracts, typeFilter, searchString, showOtherPeoplesContracts, true, sortByField, isAscending, true));
            dispatch(getTotalNumberOfContractsWithFilter(showArchivedContracts, typeFilter, searchString, showOtherPeoplesContracts));
        });
};

function getContractsFromRawContracts(contracts: Contract[], counties: Fylke[]) {
    let mapped: ListContract[] = contracts.map((contractRaw) => {
        let county: string;
        if (contractRaw.county !== undefined) {
            let mappedCounty = counties.filter(county => {
                return county.nummer == contractRaw.county;
            })[0];
            county = mappedCounty ? mappedCounty.navn : "";
        }
        const road = `${(contractRaw.roadCategory ? contractRaw.roadCategory : "")}${(contractRaw.roadStatus ? contractRaw.roadStatus : "")}${(contractRaw.roadNumber ? contractRaw.roadNumber : "")}`;
        // Create a single string from all searchable terms
        const searchIndex = (`${contractRaw.name} ${contractRaw.assignee} ${contractRaw.assigneeDisplayName} ${contractRaw.county} ` +
            `${contractRaw.municipality} ${contractRaw.referenceId} ${road} ` +
            `${contractRaw.contractor} ${contractRaw.constructionManager}`).toLowerCase();
        return {
            archivedState: contractRaw.archivedState,
            assignee: contractRaw.assignee,
            assigneeDisplayName: contractRaw.assigneeDisplayName,
            comments: contractRaw.comments,
            constructionManager: contractRaw.constructionManager,
            contractor: contractRaw.contractor,
            contributors: contractRaw.contributors,
            county: county,
            municipality: contractRaw.municipality,
            fkbObjectList: contractRaw.fkbObjectList,
            id: contractRaw.id,
            lastModified: contractRaw.lastModified,
            mapComments: contractRaw.mapComments,
            milestones: contractRaw.milestones,
            name: contractRaw.name,
            referenceId: contractRaw.referenceId,
            road: road,
            status: contractRaw.status,
            contractType: contractRaw.contractType,
            completionDate: contractRaw.completionDate,
            searchIndex: searchIndex,
            canDelete: false,
            canArchive: false,
            hasChanges: false,
            contractGroup: contractRaw.contractGroup
        }
    });
    return mapped;
}

export type FetchContracts = (
    page: number,
    showArchived: boolean,
    contractType: ContractType | "all",
    searchTerm: string,
    showOtherPeoplesContracts: boolean,
    replaceCurrent: boolean,
    sortByField: string,
    isAscending: boolean,
    checkAllContractsForChanges: boolean
) => ContractListThunk;
export const fetchContracts: FetchContracts = (
    page: number,
    showArchived: boolean,
    contractType: ContractType | "all",
    searchTerm,
    showOtherPeoplesContracts,
    replaceCurrent,
    sortByField,
    isAscending,
    doCheckAllContractsForChanges
) => async (dispatch, getState, serverHolder) => {

    dispatch({
        type: ContractlistActionType.FETCHING_CONTRACTS,
        fetching: true
    });

    serverHolder.$q.all([serverHolder.nvdbapi.getCounties(), serverHolder.server.listContracts(false, page, showArchived, contractType, searchTerm, showOtherPeoplesContracts, sortByField, isAscending)])
        .then(results => {
            let counties = results[0].data;
            let rawContracts = results[1];

            if (rawContracts) {
                let mapped = getContractsFromRawContracts(rawContracts, counties);
                let contracts;
                if (replaceCurrent) {
                    contracts = mapped;
                } else {
                    contracts = getState().contracts.concat(mapped);
                }

                dispatch({
                    type: ContractlistActionType.SET_CONTRACTS,
                    contracts: contracts,
                });

                dispatch({
                    type: ContractlistActionType.FETCHING_CONTRACTS,
                    fetching: false
                });

                if (doCheckAllContractsForChanges) {
                    dispatch(checkAllContractsForChanges(contracts))
                } else {
                    dispatch(checkNewContractsForChanges(contracts))
                }
            }
        })
};

export type CheckAllContractsForChanges = (contracts: ListContract[]) => ContractListThunk;
export const checkAllContractsForChanges: CheckAllContractsForChanges = (contracts) => async (dispatch, getState, serverHolder) => {
    const contractIds = contracts.map(contract => contract.id);
    if (contractIds.length > 0) {
        serverHolder.server.checkIfContractsHaveChanges(contractIds)
            .then(data => {
                dispatch({
                    type: ContractlistActionType.SET_CONTRACTS_WITH_CHANGE_INDICATOR,
                    hasChanges: data
                })
            })
    }

};

export type CheckNewContractsForChanges = (contracts: ListContract[]) => ContractListThunk;
export const checkNewContractsForChanges: CheckNewContractsForChanges = (contracts) => async (dispatch, getState, serverHolder) => {
    const contractsCheckedForChanges = getContractsCheckedForChanges(getState());
    const contractsToCheckForChanges = contracts.filter(contract => !contractsCheckedForChanges.includes(contract.id));
    const contractIds = contractsToCheckForChanges.map(contract => contract.id);
    if (contractIds.length > 0) {
        serverHolder.server.checkIfContractsHaveChanges(contractIds)
            .then(data => {
                dispatch({
                    type: ContractlistActionType.ADD_CONTRACTS_WITH_CHANGE_INDICATOR,
                    contractsWithChanges: data
                })
            })
    }
};

export type ArchiveContract = (contractId: string, newArchivedState: ArchivedState) => ContractListThunk;
export const archiveContract: ArchiveContract = (contractId, newArchivedState) => async (dispatch, getState, serverHolder) => {
    dispatch({
        type: ContractlistActionType.FETCHING_CONTRACTS,
        fetching: true
    });

    serverHolder.server.toggleArchiveContract(contractId, newArchivedState)
        .then((response: IHttpResponse<void>) => {
                // No response object, only HTTP status
                if (response.status === 204) {
                    const {showArchivedContracts, typeFilter, searchString, showOtherPeoplesContracts, sortByField, isAscending} = getState();
                    dispatch(fetchContracts(1, showArchivedContracts, typeFilter, searchString, showOtherPeoplesContracts, true, sortByField, isAscending, false))
                } else {
                    dispatch(showErrorMessage("Klarte ikke å arkivere kontrakten"));
                }
        })
        .finally(() => {
            dispatch({
                type: ContractlistActionType.FETCHING_CONTRACTS,
                fetching: false
            });

            const {showArchivedContracts, typeFilter, searchString, showOtherPeoplesContracts} = getState();
            dispatch(getTotalNumberOfContractsWithFilter(showArchivedContracts,typeFilter, searchString, showOtherPeoplesContracts));
        });

};

export type DeleteContract = (contractId: string) => ContractListThunk;
export const deleteContract: DeleteContract = (contractId) => async (dispatch, getState) => {

    dispatch({
        type: ContractlistActionType.FETCHING_CONTRACTS,
        fetching: true
    });

    const response = await httpDelete('/rest/contract/' + contractId);

    if (response.success || response.httpStatus === 404) {
        const contracts = getState().contracts.filter(contract => contract.id !== contractId)
            .slice(0);
        dispatch({
            type: ContractlistActionType.SET_CONTRACTS,
            contracts
        });
        const {showArchivedContracts, typeFilter, searchString, showOtherPeoplesContracts} = getState();
        dispatch(getTotalNumberOfContractsWithFilter(showArchivedContracts,typeFilter, searchString, showOtherPeoplesContracts));
    }

    if (!response.success) {

        let errorMessage;

        switch (response.httpStatus) {
            case 404:
                errorMessage = "Kontrakten du prøver å slette finnes ikke lenger";
                break;
            case 403:
                errorMessage = "Du har ikke rettigheter til å slette denne kontrakten";
                break;
            default:
                errorMessage = "Klarte ikke å slette kontrakten";
                if (response.message) {
                    errorMessage += ` (${response.message})`;
                }
        }
        dispatch(showErrorMessage(errorMessage));
    }

    dispatch({
        type: ContractlistActionType.FETCHING_CONTRACTS,
        fetching: false
    });
};

export type SetSearchString = (searchString: string) => ContractListThunk;
export const setSearchString: SetSearchString = (searchString) => (dispatch) => {
    dispatch({
        type: ContractlistActionType.SET_SEARCH_STRING,
        searchString,
    })
};

export type ToggleArchivedFilter = () => ContractListThunk;
export const toggleArchivedFilter: ToggleArchivedFilter = () => (dispatch, getState) => {
    dispatch({
        type: ContractlistActionType.SET_ARCHIVED_FILTER,
        showArchivedContracts: !getState().showArchivedContracts
    })
};

export type ToggleShowOtherPeoplesContracts = () => ContractListThunk;
export const toggleShowOtherPeoplesContracts: ToggleShowOtherPeoplesContracts = () => (dispatch, getState) => {
    dispatch({
        type: ContractlistActionType.SET_FILTER_BY_CURRENT_USER,
        showOtherPeoplesContracts: !getState().showOtherPeoplesContracts
    })
};

export type SetTypeFilter = (typeFilter: string) => ContractListThunk;
export const setTypeFilter: SetTypeFilter = (typeFilter) => (dispatch) => {
    dispatch({
        type: ContractlistActionType.SET_FILTER_BY_TYPE,
        typeFilter: typeFilter,
    })
};


export type NextPage = () => ContractListThunk;
export const nextPage: NextPage = () => (dispatch, getState) => {

    dispatch(
        {
            type: ContractlistActionType.SET_PAGE,
            page: getState().page + 1
        }
    )
};


export type FirstPage = () => ContractListThunk;
export const firstPage: FirstPage = () => (dispatch) => {

    dispatch(
        {
            type: ContractlistActionType.SET_PAGE,
            page: 1
        }
    )
};


export type GetTotalNumberOfContractsWithFilter = (showArchived: boolean, contractType: ContractType | "all", searchTerm: string, showOtherPeoplesContracts: boolean) => ContractListThunk;
export const getTotalNumberOfContractsWithFilter: GetTotalNumberOfContractsWithFilter = (showArchived, contractType, searchTerm, showOtherPeoplesContracts) => (dispatch, getState, serverHolder) => {

    serverHolder.server.countContractsWithFilter(showArchived, contractType, searchTerm, showOtherPeoplesContracts)
        .then(totalNumberOfContractsWithFilter => {
            dispatch({
                type: ContractlistActionType.SET_TOTAL_NUMBER_OF_CONTRACTS_WITH_FILTER,
                totalNumberOfContractsWithFilter
            })
        });

};

export type SetSort = (sortByField: string) => ContractListThunk;
export const setSort: SetSort = (sortByField) => (dispatch, getState) => {
    if (getState().sortByField === sortByField) {
        dispatch(
            {
                type: ContractlistActionType.SET_SORT_ASCENDING,
                isAscending: !getState().isAscending
            }
        )

    } else {
        dispatch(
            {
                type: ContractlistActionType.SET_SORT_FIELD,
                sortByField
            }
        )
        dispatch(
            {
                type: ContractlistActionType.SET_SORT_ASCENDING,
                isAscending: true
            }
        )
    }
};

async function httpDelete(url: string): Promise<ApiResult<void>> {
    try {
        // Note! Does not handle logged out user (HTTP 401)
        let response = await fetch(url, {method: "DELETE", credentials: "same-origin"});
        return unwrap(response);
    } catch (error) {
        return {
            success: false,
            httpStatus: -1,
            data: null,
            message: "Nettverksfeil"
        };
    }
}

type ApiResult<T> = {
    success: boolean,
    httpStatus: number,
    data: T,
    message: string
}

async function unwrap<T>(response: Response): Promise<ApiResult<T>> {
    let result: ApiResult<T> = {
        success: response.ok,
        httpStatus: response.status,
        data: null,
        message: null
    };

    if (response.ok && response.status !== 204) {
        let json = await response.json();
        result.data = json;
        return result;
    }
    if (response)
        return result;
}
